import React, { useState, useEffect } from "react";
import { useSupplierProcessingStore } from "../../../store/useSupplierProcessingStore.ts";
import { useShipmentDetail } from "../../../features/supplier-fulfillment/hooks/useShipmentDetail";
import { useApproveShipment } from "../../../features/supplier-fulfillment/hooks/useApproveShipment";
import { useReadyShipment } from "../../../features/supplier-fulfillment/hooks/useReadyShipment";
import { useDeliverShipment } from "../../../features/supplier-fulfillment/hooks/useDeliverShipment";
import { useHandoverShipment } from "../../../features/supplier-fulfillment/hooks/useHandoverShipment";
import {formatDate} from "../../../utils/jalaliDate"

const ShipmentDetailModal = () => {
  const shipmentCode = useSupplierProcessingStore((s) => s.selectedShipment);
  const isOpen = useSupplierProcessingStore((s) => s.isModalOpen);
  const closeModal = useSupplierProcessingStore((s) => s.closeShipmentModal);

  const { data: shipment, isLoading } = useShipmentDetail(shipmentCode);
  const [formItems, setFormItems] = useState([]);
  const [deliveryCode, setDeliveryCode] = useState("");


  const approveMutation = useApproveShipment();
  const readyMutation = useReadyShipment();
  const handoverMutation = useHandoverShipment();
  const deliverMutation = useDeliverShipment();
  const UNIT_TRANSLATIONS = {
  'count': 'عدد',
  'package': 'بسته',
  'weight': 'کیلو گرم',
  'length': 'متر',
  'area': 'متر مربع',
  'litr': 'لیتر',
  'volume': 'متر مکعب'
  };

  const getStatusDisplay = (status) => {
    if (!status) return { text: 'نامشخص', color: 'text-gray-400' }; // هندل کردن مقدار خالی
    switch (status) {
      case 'PENDING_APPROVING':
        return { text: 'در انتظار تایید', color: 'text-yellow-600 bg-yellow-50 border-yellow-200' };
      case 'PENDING_PROCESSING':
        return { text: 'در انتظار آماده سازی', color: 'text-blue-600 bg-blue-50 border-blue-200' };
      case 'READY_FOR_HANDOFF':
        return { text: 'در انتظار حمل', color: 'text-green-600 bg-green-50 border-green-200' };
      case 'IN_TRANSIT':
        return { text: 'در حال ارسال', color: 'text-purple-600 bg-purple-50 border-purple-200' };
      default:
        return { text: status, color: 'text-gray-600 bg-gray-50 border-gray-200' };
    }
  };



  useEffect(() => {
    if (shipment?.items) {
      setFormItems(
        shipment.items.map((item) => ({
          item_code: item.item_code,
          qty_shipped: 0,
          is_active: false,
        }))
      );
    }
  }, [shipment]);



  if (!isOpen || !shipment) return null;
  const status = getStatusDisplay(shipment.status);

  const isPendingApprove = shipment.status === "PENDING_APPROVING";

  const handleToggleItem = (code) => {
    setFormItems((prev) =>
      prev.map((item) =>
        item.item_code === code ? { ...item, is_active: !item.is_active } : item
      )
    );
  };

  const handleQtyChange = (code, val) => {
    const numVal = Math.max(0, Number(val));
    setFormItems((prev) =>
      prev.map((item) =>
        item.item_code === code ? { ...item, qty_shipped: numVal } : item
      )
    );
  };

  const isApproveDisabled =
    formItems.length === 0 ||
    formItems.some(item =>
      !item.is_active || item.qty_shipped === "" || item.qty_shipped === null || item.qty_shipped === undefined
    );


  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-5xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] rtl">
        <div className="flex justify-between items-start m-[24px]">
          <div className="flex items-center gap-2">
            <span className="text-gray-500 text-sm">کد محموله</span>
            <span className="font-bold text-gray-800">{shipment.shipment_code.substring(0, 8)}</span>
          </div>
          <div>
            {shipment.delay_days > 0 && (
                <div className="flex items-center gap-1 bg-red-600 text-white rounded-[8px] py-[4px] px-[8px] text-[14px]">
            <span>
              {shipment.delay_days}
            </span>
                  <span> روز تأخیر</span>
                </div>
            )}
          </div>

          <div className={`px-4 py-1 rounded-full border text-sm font-medium ${status.color}`}>
            {status.text}
          </div>
        </div>
        {/* Header Info Grid */}
        <div className="p-8 border-b border-gray-100 bg-gray-50/50">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-y-6 gap-x-12 relative">

            {/* Status Badge - Top Left */}
            <InfoField label="کد سفارش" value={shipment.order_code?.split("-")[0]}/>
            <InfoField label="زمان ثبت" value={formatDate(shipment.created_at)}/>
            <InfoField label="شهر-استان" value={shipment.destination}/>
            <InfoField label="نام مشتری" value={shipment.receiver_full_name}/>
            <InfoField label="تلفن مشتری" value={shipment.receiver_phone}/>
            <InfoField label="وزن کل" value={`${shipment.total_weight} گرم`}/>

          </div>
        </div>

        {/* Items Table */}
        <div className="flex-1 overflow-y-auto p-8">
          <div className="grid grid-cols-12 gap-4 mb-4 px-4 text-gray-400 text-sm font-medium">
            <div className="col-span-4">نام کالا</div>
            <div className="col-span-2 text-center">دسته</div>
            <div className="col-span-1 text-center">برند</div>
            <div className="col-span-2 text-center">قیمت واحد (تومان)</div>
            <div className="col-span-1 text-center">تعداد</div>
            <div className="col-span-2 text-center">تعداد تایید شده</div>
          </div>

          <div className="space-y-3">
            {shipment.items.map((item) => {
              const currentFormItem = formItems.find((f) => f.item_code === item.item_code);
              return (
                  <div key={item.item_code}
                       className="grid grid-cols-12 gap-4 items-center p-4 border border-gray-100 rounded-xl hover:bg-gray-50 transition-colors">
                    <div className="col-span-4 flex items-center gap-4">
                      <img src={item.product_image} alt="" className="w-14 h-14 rounded-lg object-cover bg-gray-100"/>
                      <div className="flex flex-col">
                        <span className="text-gray-800 font-bold text-sm">{item.product_name}</span>
                      </div>
                    </div>
                    <div className="col-span-2 text-center text-gray-600 text-sm">{item.product_category}</div>
                    <div className="col-span-1 text-center text-gray-600 text-sm">{item.product_brand}</div>
                    <div
                        className="col-span-2 text-center text-gray-800 font-medium text-sm">{Number(item.unit_price).toLocaleString()}</div>
                    <div className="col-span-1 text-center text-gray-600 text-sm">{item.qty_ordered}
                      <span> {UNIT_TRANSLATIONS[item.unit_of_measure]}</span></div>

                    <div className="col-span-2 flex items-center justify-center gap-3">
                      {isPendingApprove ? (
                          <>
                            <input
                                type="number"
                                className="w-16 h-10 border border-gray-200 rounded-lg text-center text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                value={currentFormItem?.qty_shipped || ""}
                                onChange={(e) => handleQtyChange(item.item_code, e.target.value)}
                            />
                            <button
                                onClick={() => handleToggleItem(item.item_code)}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${currentFormItem?.is_active ? 'bg-green-500' : 'bg-gray-300'}`}
                            >
                              <span
                                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${currentFormItem?.is_active ? '-translate-x-6' : '-translate-x-1'}`}/>
                            </button>
                          </>
                      ) : (
                          <div className="bg-gray-100 px-3 py-1.5 rounded-lg text-gray-500 text-sm font-medium">
                            {item.qty_shipped} تایید شده
                          </div>
                      )}
                    </div>
                  </div>
              );
            })}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-6 bg-white border-t border-gray-100 flex items-center gap-3">
          {shipment.status === "IN_TRANSIT" && (
              <div className="flex items-center gap-3 ml-auto">
                <span className="text-sm text-gray-600">کد تحویل</span>

                <input
                    type="text"
                    value={deliveryCode}
                    onChange={(e) => setDeliveryCode(e.target.value)}
                    className="w-32 h-10 border border-gray-200 rounded-lg text-center text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                    placeholder="۶ رقم"
                    maxLength={6}
                />
              </div>
          )}

          <button
              onClick={closeModal}
              className="px-8 py-2.5 rounded-xl border-2 border-red-500 text-red-500 font-bold hover:bg-red-50 transition-all text-sm"
          >
            انصراف
          </button>

          {shipment.status === "PENDING_APPROVING" && (
              <button
                  disabled={isApproveDisabled}
                  onClick={() => {
                    const itemsToSubmit = formItems.filter(i => i.is_active).map(i => ({
                      item_code: i.item_code,
                      qty_shipped: i.qty_shipped
                    }));
                    approveMutation.mutate({shipmentCode, data: {items: itemsToSubmit}});
                    closeModal();
                  }}
                  className="px-10 py-2.5 rounded-xl bg-[#1e293b] text-white font-bold hover:bg-slate-800 transition-all text-sm shadow-lg shadow-slate-200"
              >
                تایید
              </button>
          )}

          {shipment.status === "PENDING_PROCESSING" && (
              <button
                  onClick={() => {
                    readyMutation.mutate(shipmentCode);
                    closeModal();
                  }}
                  className="px-10 py-2.5 rounded-xl border-2 border-[#1e293b] text-[#1e293b] font-bold hover:bg-slate-50 transition-all text-sm"
              >
                آماده شده
              </button>
          )}

          {shipment.status === "READY_FOR_HANDOFF" && (
              <button
                  onClick={() => {
                    handoverMutation.mutate(shipmentCode);
                    closeModal();
                  }}
                  className="px-10 py-2.5 rounded-xl border-2 border-[#1e293b] text-[#1e293b] font-bold hover:bg-slate-50 transition-all text-sm"
              >
                تحویل شده
              </button>
          )}

          {shipment.status === "IN_TRANSIT" && (
            <button
                disabled={deliveryCode.length !== 6}
                onClick={() => {
                  deliverMutation.mutate({
                    shipmentCode,
                    data: {delivery_code: deliveryCode}
                  });
                  closeModal();
                }}
                className="px-10 py-2.5 rounded-xl bg-green-600 text-white font-bold hover:bg-green-700 transition-all text-sm"
            >
              تحویل به مشتری
            </button>
              )}
        </div>
      </div>
    </div>
  );
};

// Helper Component for Header Fields
const InfoField = ({label, value}) => (
    <div className="flex flex-col gap-1">
      <span className="text-gray-400 text-xs font-medium">{label}</span>
      <span className="text-gray-800 font-bold text-sm tracking-wide">{value || "---"}</span>
    </div>
);

export default ShipmentDetailModal;
